import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { EnrolmentUiProvider } from "@alight/enrolment-ui";
import { router } from "./router";
import "./styles/index.css";
import { installStudioPreview, registerMarks } from "@alight/studio-preview";
import { LogoMark } from "./components/Logo";

// Studio route announcer (dev only — never in the production build): inside
// the Studio shell's iframe, tell the parent which page is showing.
if (import.meta.env.DEV && window.parent !== window) {
  let last = "";
  const announce = () => {
    const path = window.location.pathname + window.location.search;
    if (path === last) return;
    last = path;
    window.parent.postMessage({ type: "studio:route", path }, "*");
  };
  announce();
  window.setInterval(announce, 500);
}

// Under the prerenderer (headless Chromium, navigator.webdriver=true) API
// calls are aborted at the network layer; skip react-query's retry backoff so
// pages settle on their static fallback content immediately.
const queryClient = new QueryClient(
  typeof navigator !== "undefined" && navigator.webdriver
    ? { defaultOptions: { queries: { retry: false } } }
    : {},
);

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found");

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* No `brands`: this site IS the organisation. Registration (if a paid
          program is ever added in the host portal) lives at /register; "what's
          on" is the Sessions page. */}
      <EnrolmentUiProvider
        config={{
          siteName: "Connect Sydney",
          registerPath: "/register",
          programsPath: "/sessions",
        }}
      >
        <RouterProvider router={router} />
      </EnrolmentUiProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);

// Studio branding-mode chrome. Dev only — compiled out of production builds.
if (import.meta.env.DEV) {
  installStudioPreview();
  // Lend the Studio this site's mark so an option naming it draws it in that
  // option's OWN colours (the mark reads its two tones from CSS variables).
  registerMarks(["wordmark"], (_mark, vars) => (
    <span
      style={
        {
          "--logo-core": `rgb(${vars["--color-primary"] ?? "0 0 0"})`,
          "--logo-cool": `rgb(${vars["--color-secondary"] ?? vars["--color-accent"] ?? "0 0 0"})`,
        } as React.CSSProperties
      }
    >
      <LogoMark className="text-3xl" />
    </span>
  ));
}
